import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import {
  TvIcon,
  MagnifyingGlassIcon,
  ComputerDesktopIcon,
  SignalIcon,
  PlayIcon,
  StopIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  XMarkIcon,
  CursorArrowRaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SOCKET_URL = API_URL.replace('/api', '');

// WebRTC Configuration with our own TURN server
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    // Our own TURN server for NAT traversal
    {
      urls: 'turn:37.27.209.219:3478',
      username: 'mimdesk',
      credential: 'MimDesk2024!'
    },
    {
      urls: 'turn:37.27.209.219:3478?transport=tcp',
      username: 'mimdesk',
      credential: 'MimDesk2024!'
    }
  ],
  iceCandidatePoolSize: 10
};

const MimDeskPage = () => {
  const [socket, setSocket] = useState(null);
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isControlEnabled, setIsControlEnabled] = useState(false);
  const [connectionRequest, setConnectionRequest] = useState(null);

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const canvasRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  const remoteDescriptionSetRef = useRef(false);

  // Initialize socket connection
  useEffect(() => {
    if (!token) return;

    const newSocket = io(SOCKET_URL, {
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('MimDesk socket connected');
      // Register as controller (IT/Admin)
      newSocket.emit('mimdesk:register-controller');
    });

    // Receive list of online agents
    newSocket.on('mimdesk:agents-list', (agentsList) => {
      setAgents(agentsList);
    });

    // Agent came online
    newSocket.on('mimdesk:agent-online', (agent) => {
      setAgents(prev => {
        const exists = prev.find(a => a.agentId === agent.agentId);
        if (exists) {
          return prev.map(a => a.agentId === agent.agentId ? agent : a);
        }
        return [...prev, agent];
      });
    });

    // Agent went offline
    newSocket.on('mimdesk:agent-offline', ({ agentId }) => {
      setAgents(prev => prev.filter(a => a.agentId !== agentId));
      if (selectedAgent?.agentId === agentId) {
        handleDisconnect();
      }
    });

    // Connection accepted by agent
    newSocket.on('mimdesk:connection-accepted', async (data) => {
      console.log('Connection accepted event received:', JSON.stringify(data));
      const { agentId, agentSocketId, agentUserId } = data;
      console.log('Parsed - agentId:', agentId, 'agentSocketId:', agentSocketId, 'agentUserId:', agentUserId);

      if (!agentSocketId) {
        console.error('ERROR: agentSocketId is undefined!');
        return;
      }

      setConnectionStatus('connecting');
      // Start WebRTC connection
      console.log('Calling initiateWebRTC with socket and agentSocketId:', agentSocketId);
      await initiateWebRTC(newSocket, agentSocketId);
    });

    // Connection rejected by agent
    newSocket.on('mimdesk:connection-rejected', ({ reason }) => {
      setConnectionStatus('disconnected');
      alert(`Bağlantı reddedildi: ${reason || 'Kullanıcı izin vermedi'}`);
    });

    // Receive WebRTC offer from agent
    newSocket.on('mimdesk:webrtc-offer', async ({ senderSocketId, offer }) => {
      if (peerConnectionRef.current) {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await peerConnectionRef.current.createAnswer();
        await peerConnectionRef.current.setLocalDescription(answer);
        newSocket.emit('mimdesk:webrtc-answer', { targetSocketId: senderSocketId, answer });
      }
    });

    // Receive WebRTC answer from agent
    newSocket.on('mimdesk:webrtc-answer', async (data) => {
      console.log('Received webrtc-answer event:', JSON.stringify(data));
      const { answer, senderSocketId } = data;
      console.log('Answer object:', answer);

      if (!answer || !answer.type || !answer.sdp) {
        console.error('Invalid answer received:', answer);
        return;
      }

      if (peerConnectionRef.current) {
        try {
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
          console.log('Remote description set successfully');
          remoteDescriptionSetRef.current = true;

          // Process any pending ICE candidates
          if (pendingIceCandidatesRef.current.length > 0) {
            console.log(`Processing ${pendingIceCandidatesRef.current.length} pending ICE candidates`);
            for (const candidate of pendingIceCandidatesRef.current) {
              try {
                await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
                console.log('Pending ICE candidate added successfully');
              } catch (e) {
                console.log('Error adding pending ICE candidate:', e);
              }
            }
            pendingIceCandidatesRef.current = [];
          }
        } catch (error) {
          console.error('Error setting remote description:', error);
        }
      }
    });

    // Receive ICE candidate
    newSocket.on('mimdesk:ice-candidate', async ({ candidate }) => {
      if (candidate) {
        // If remote description is not set yet, queue the candidate
        if (!remoteDescriptionSetRef.current) {
          console.log('Queueing ICE candidate (remote description not set yet)');
          pendingIceCandidatesRef.current.push(candidate);
          return;
        }

        if (peerConnectionRef.current) {
          try {
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
            console.log('ICE candidate added successfully');
          } catch (e) {
            console.log('ICE candidate error:', e);
          }
        }
      }
    });

    // Session ended by agent
    newSocket.on('mimdesk:session-ended', () => {
      handleDisconnect();
      alert('Uzaktan destek oturumu sonlandırıldı');
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [token]);

  // Initialize WebRTC connection
  const initiateWebRTC = async (sock, agentSocketId) => {
    console.log('initiateWebRTC called with agentSocketId:', agentSocketId);
    console.log('Socket connected:', sock?.connected);

    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionRef.current = pc;
    console.log('RTCPeerConnection created');

    // Handle incoming stream (screen share from agent)
    pc.ontrack = (event) => {
      console.log('Received remote track:', event.streams);
      if (videoRef.current && event.streams[0]) {
        videoRef.current.srcObject = event.streams[0];
        remoteStreamRef.current = event.streams[0];
        setConnectionStatus('connected');
        console.log('Video stream set, status: connected');
      }
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && agentSocketId) {
        console.log('Sending ICE candidate to agent');
        sock.emit('mimdesk:ice-candidate', {
          targetSocketId: agentSocketId,
          candidate: event.candidate
        });
      }
    };

    // Monitor connection state
    pc.onconnectionstatechange = () => {
      console.log('WebRTC Connection state:', pc.connectionState);
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        handleDisconnect();
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('ICE Connection state:', pc.iceConnectionState);
    };

    // Add a transceiver to receive video
    pc.addTransceiver('video', { direction: 'recvonly' });
    console.log('Video transceiver added');

    // Create and send offer to agent
    try {
      console.log('Creating WebRTC offer...');
      const offer = await pc.createOffer();
      console.log('Offer created, setting local description...');
      await pc.setLocalDescription(offer);
      console.log('Local description set, emitting webrtc-offer to:', agentSocketId);
      sock.emit('mimdesk:webrtc-offer', {
        targetSocketId: agentSocketId,
        offer: pc.localDescription
      });
      console.log('WebRTC offer emitted successfully');
    } catch (error) {
      console.error('Error creating/sending offer:', error);
    }
  };

  // Request connection to agent
  const requestConnection = (agent) => {
    if (!socket) return;

    setSelectedAgent(agent);
    setConnectionStatus('connecting');

    socket.emit('mimdesk:request-connection', {
      agentId: agent.agentId
    });
  };

  // Disconnect from agent
  const handleDisconnect = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach(track => track.stop());
      remoteStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    // Reset ICE candidate queue
    pendingIceCandidatesRef.current = [];
    remoteDescriptionSetRef.current = false;

    setConnectionStatus('disconnected');
    setSelectedAgent(null);
    setIsControlEnabled(false);

    if (socket && selectedAgent) {
      socket.emit('mimdesk:end-session', { targetSocketId: selectedAgent.socketId });
    }
  };

  // Send mouse event to agent
  const sendMouseEvent = useCallback((event, type) => {
    if (!socket || !selectedAgent || !isControlEnabled) return;

    const video = videoRef.current;
    if (!video) return;

    const rect = video.getBoundingClientRect();
    const scaleX = video.videoWidth / rect.width;
    const scaleY = video.videoHeight / rect.height;

    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    socket.emit('mimdesk:mouse-event', {
      targetSocketId: selectedAgent.socketId,
      event: {
        type,
        x: Math.round(x),
        y: Math.round(y),
        button: event.button
      }
    });
  }, [socket, selectedAgent, isControlEnabled]);

  // Send keyboard event to agent
  const sendKeyEvent = useCallback((event, type) => {
    if (!socket || !selectedAgent || !isControlEnabled) return;

    event.preventDefault();

    socket.emit('mimdesk:keyboard-event', {
      targetSocketId: selectedAgent.socketId,
      event: {
        type,
        key: event.key,
        code: event.code,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey
      }
    });
  }, [socket, selectedAgent, isControlEnabled]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Filter agents by search term
  const filteredAgents = agents.filter(agent =>
    agent.computerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.ipAddress?.includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                <TvIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">MimDesk</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Uzaktan Masaustu Destek</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {agents.length} cihaz cevrimici
              </span>
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-6">
        {!selectedAgent ? (
          /* Agent List View */
          <div className="max-w-6xl mx-auto">
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Bilgisayar adi, kullanici adi veya IP adresi ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Agent Cards */}
            {filteredAgents.length === 0 ? (
              <div className="text-center py-12">
                <ComputerDesktopIcon className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {searchTerm ? 'Sonuc bulunamadi' : 'Henuz bagli cihaz yok'}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm
                    ? 'Farkli bir arama terimi deneyin'
                    : 'MimDesk Agent kurulu bilgisayarlar burada gorunecek'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAgents.map((agent) => (
                  <div
                    key={agent.agentId}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                          <ComputerDesktopIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {agent.computerName || 'Bilinmeyen'}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {agent.username || 'Kullanici yok'}
                          </p>
                        </div>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900 px-2 py-1 rounded-full">
                        <SignalIcon className="h-3 w-3" />
                        Online
                      </span>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <div className="flex justify-between">
                        <span>IP Adresi:</span>
                        <span className="font-mono">{agent.ipAddress || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Isletim Sistemi:</span>
                        <span>{agent.osInfo || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Kullanici:</span>
                        <span>{agent.userFullName || '-'}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => requestConnection(agent)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    >
                      <PlayIcon className="h-4 w-4" />
                      Baglan
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Instructions */}
            <div className="mt-8 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-3">
                MimDesk Agent Kurulumu
              </h3>
              <p className="text-indigo-800 dark:text-indigo-200 mb-4">
                Uzaktan destek almak isteyen bilgisayarlara MimDesk Agent kurulmalidir.
              </p>
              <div className="flex gap-3">
                <a
                  href="https://github.com/osultanoglu-cpu/mim-it-talep-desktop/releases/download/v2.0.6/MIM.IT.Talep.Setup.2.0.6.exe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                  Windows icin Indir (v2.0.6)
                </a>
              </div>
            </div>
          </div>
        ) : (
          /* Remote Desktop View */
          <div ref={containerRef} className="max-w-full mx-auto">
            {/* Connection Status Bar */}
            <div className="bg-white dark:bg-gray-800 rounded-t-lg border border-b-0 border-gray-200 dark:border-gray-700 p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ComputerDesktopIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedAgent.computerName}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 mx-2">-</span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {selectedAgent.username}
                  </span>
                </div>
                {connectionStatus === 'connecting' && (
                  <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Baglaniyor...
                  </span>
                )}
                {connectionStatus === 'connected' && (
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4" />
                    Bagli
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Control Toggle */}
                <button
                  onClick={() => setIsControlEnabled(!isControlEnabled)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                    isControlEnabled
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                  title={isControlEnabled ? 'Kontrol Aktif' : 'Kontrol Pasif'}
                >
                  <CursorArrowRaysIcon className="h-4 w-4" />
                  {isControlEnabled ? 'Kontrol Aktif' : 'Sadece Izle'}
                </button>

                {/* Fullscreen */}
                <button
                  onClick={toggleFullscreen}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Tam Ekran"
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="h-5 w-5" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-5 w-5" />
                  )}
                </button>

                {/* Disconnect */}
                <button
                  onClick={handleDisconnect}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                >
                  <StopIcon className="h-4 w-4" />
                  Baglantıyı Kes
                </button>
              </div>
            </div>

            {/* Video/Screen Area */}
            <div className="relative bg-black rounded-b-lg overflow-hidden" style={{ minHeight: '60vh' }}>
              {connectionStatus === 'connecting' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <ArrowPathIcon className="h-12 w-12 animate-spin mb-4" />
                  <p className="text-lg">Baglanti bekleniyor...</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Kullanici baglanti isteginizi onaylamali
                  </p>
                </div>
              )}

              {connectionStatus === 'disconnected' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mb-4" />
                  <p className="text-lg">Baglanti kesildi</p>
                </div>
              )}

              <video
                ref={videoRef}
                autoPlay
                playsInline
                className={`w-full h-full object-contain ${connectionStatus !== 'connected' ? 'hidden' : ''}`}
                onMouseMove={(e) => sendMouseEvent(e, 'move')}
                onMouseDown={(e) => sendMouseEvent(e, 'down')}
                onMouseUp={(e) => sendMouseEvent(e, 'up')}
                onClick={(e) => sendMouseEvent(e, 'click')}
                onDoubleClick={(e) => sendMouseEvent(e, 'dblclick')}
                onContextMenu={(e) => { e.preventDefault(); sendMouseEvent(e, 'contextmenu'); }}
                onKeyDown={(e) => sendKeyEvent(e, 'down')}
                onKeyUp={(e) => sendKeyEvent(e, 'up')}
                tabIndex={0}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MimDeskPage;
